import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../../context/AuthContext';
import { getCommissionsByRealtor } from '../../../services/commissionService';
import { getRequestsByRealtor } from '../../../services/serviceRequestService';

const TYPE_LABEL = {
  conversion: '전환',
  referral: '추천',
  consulting: '상담',
};

const formatRecentDate = (c) => {
  if (c.createdAt) return c.createdAt;
  const ts = c.created_at;
  if (!ts) return '';
  const d = ts?.toDate?.() || new Date((ts?.seconds || 0) * 1000);
  return d.toISOString().slice(0, 10);
};

const Performance = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userData?.uid) {
      setLoading(false);
      return;
    }
    Promise.all([
      getCommissionsByRealtor(userData.uid).catch(() => []),
      getRequestsByRealtor(userData.uid).catch(() => []),
    ]).then(([c, r]) => {
      setCommissions(c || []);
      setRequests(r || []);
    }).finally(() => setLoading(false));
  }, [userData?.uid]);

  const { thisMonthAmount, deltaPct } = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const prevDate = new Date(y, m - 1, 1);
    const py = prevDate.getFullYear();
    const pm = prevDate.getMonth();

    let cur = 0;
    let prev = 0;
    commissions.forEach((c) => {
      const ts = c.created_at;
      const d = ts?.toDate?.() || (ts?.seconds ? new Date(ts.seconds * 1000) : null);
      if (!d) return;
      if (d.getFullYear() === y && d.getMonth() === m) cur += c.amount || 0;
      else if (d.getFullYear() === py && d.getMonth() === pm) prev += c.amount || 0;
    });

    let pct = 0;
    if (prev === 0 && cur === 0) pct = 0;
    else if (prev === 0) pct = 100;
    else pct = ((cur - prev) / prev) * 100;
    return { thisMonthAmount: cur, deltaPct: pct };
  }, [commissions]);

  const counts = useMemo(() => {
    let progress = 0;
    let done = 0;
    requests.forEach((r) => {
      const s = r.hq_status || r.hqStatus || '';
      if (r.serviceCompleted || s === 'settlement_done' || s === 'completed') done += 1;
      else if (s === 'assigned' || s === 'settlement_check') progress += 1;
    });
    const pendingSettlement = commissions.filter((c) => !c.is_settled).length;
    return { progress, done, pendingSettlement };
  }, [requests, commissions]);

  const recent = useMemo(() => commissions.slice(0, 5), [commissions]);

  const deltaSign = deltaPct > 0 ? '+' : '';

  return (
    <Container>
      <BlueCard>
        <BlueCardLabel>이번 달 리워드</BlueCardLabel>
        <BlueCardAmount>{thisMonthAmount.toLocaleString()}원</BlueCardAmount>
        <BlueCardSub>전월 대비 {deltaSign}{deltaPct.toFixed(1)}%</BlueCardSub>
      </BlueCard>

      <StatRow>
        <StatItem>
          <StatNum>{counts.progress}</StatNum>
          <StatLabel>진행중</StatLabel>
        </StatItem>
        <StatDivider />
        <StatItem>
          <StatNum>{counts.done}</StatNum>
          <StatLabel>완료</StatLabel>
        </StatItem>
        <StatDivider />
        <StatItem>
          <StatNum>{counts.pendingSettlement}</StatNum>
          <StatLabel>정산대기</StatLabel>
        </StatItem>
      </StatRow>

      <SectionHeader>
        <SectionTitle>최근 리워드</SectionTitle>
        <SectionLink onClick={() => navigate('/realtor/mypage/commission')}>전체 &gt;</SectionLink>
      </SectionHeader>

      {loading ? (
        <EmptyBox>
          <EmptyIcon>…</EmptyIcon>
          <EmptyTitle>불러오는 중...</EmptyTitle>
        </EmptyBox>
      ) : recent.length === 0 ? (
        <EmptyBox>
          <EmptyIcon>!</EmptyIcon>
          <EmptyTitle>리워드 내역이 없어요</EmptyTitle>
        </EmptyBox>
      ) : (
        <RecentList>
          {recent.map((c) => (
            <RecentRow key={c.id}>
              <RecentLeft>
                <RecentType>{TYPE_LABEL[c.type] || c.type || '리워드'}</RecentType>
                <RecentDate>{formatRecentDate(c)}</RecentDate>
              </RecentLeft>
              <RecentAmount>{(c.amount || 0).toLocaleString()}원</RecentAmount>
            </RecentRow>
          ))}
        </RecentList>
      )}

      <BtnRow>
        <OutlineBtn onClick={() => navigate('/realtor/mypage/reward-stats')}>통계 보기</OutlineBtn>
        <OutlineBtn onClick={() => navigate('/realtor/mypage/customer-db-stats')}>고객 DB 실적</OutlineBtn>
      </BtnRow>
    </Container>
  );
};

export default Performance;

const Container = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const BlueCard = styled.div`
  background: #F0F5FF;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const BlueCardLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const BlueCardAmount = styled.p`
  font-size: 28px;
  font-weight: ${({ theme }) => theme.fontWeight.extrabold};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const BlueCardSub = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.primary};
`;

const StatRow = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.base} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const StatItem = styled.div`
  flex: 1;
  text-align: center;
`;

const StatNum = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const StatLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const StatDivider = styled.div`
  width: 1px;
  height: 32px;
  background: ${({ theme }) => theme.colors.borderLight};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const SectionTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const SectionLink = styled.button`
  background: none;
  border: none;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
`;

const EmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xxl} 0;
`;

const EmptyIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.colors.bg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.xl};
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const EmptyTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const RecentList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
`;

const RecentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.base};
  background: #fff;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};

  &:first-of-type { border-top: none; }
`;

const RecentLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const RecentType = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const RecentDate = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

const RecentAmount = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const BtnRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.base};
`;

const OutlineBtn = styled.button`
  flex: 1;
  height: 48px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.bgWhite};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;
`;
